/**
 * Pure derivations for the M1a "Mietverhältnisse" tab.
 *
 * Everything here reads fields the tenancy list endpoint already sends. Nothing
 * fetches, nothing decides business rules, and — deliberately — nothing reads a
 * clock. The tab used to derive its visible status from `new Date()` on the
 * viewer's machine while the Übersicht card used the server's Swiss business
 * day, so the same tenancy could read "Geplant" in one place and "Reserviert"
 * in another. The server already resolves that; we render its answer.
 */
import { tenantDisplayName } from "../../../../utils/tenantDisplayName";
import {
  tenancyDisplayEndIso,
  tenancyDisplayStatusLabelDe,
  viewerTenancyRoleLabelDe,
} from "../tenantDetailHelpers";

/** Buckets, in the order the tab presents them. */
export const TENANCY_GROUPS = ["current", "future", "historical", "cancelled"];

export const TENANCY_GROUP_LABELS = {
  current: "Aktuell",
  future: "Geplant",
  historical: "Beendet",
  cancelled: "Storniert",
};

/**
 * Which bucket a tenancy belongs to, from server fields only.
 *
 * `is_running` is the server's own answer to "is this tenancy live today",
 * resolved against the Swiss business day — not `status === "active"`, because
 * a moved-in tenancy is frequently still stored as `reserved`. `display_status`
 * carries the terminal states. A tenancy that is running AND terminated is
 * current, not historical: the notice is given, the person still lives there.
 */
export function tenancyGroup(tn) {
  const ds = String(tn?.display_status || "").toLowerCase();
  if (ds === "cancelled") return "cancelled";
  if (tn?.is_running === true) return "current";
  if (ds === "ended") return "historical";
  return "future";
}

/**
 * Group the list without reordering within a group.
 *
 * The server sorts by move_in_date DESC with a documented tie-break; re-sorting
 * here would invent a second ordering. Two simultaneously running tenancies —
 * an internal move — both land in `current`, and both are shown.
 */
export function groupTenancies(tenancies) {
  const out = { current: [], future: [], historical: [], cancelled: [] };
  if (!Array.isArray(tenancies)) return out;
  for (const tn of tenancies) {
    if (tn) out[tenancyGroup(tn)].push(tn);
  }
  return out;
}

/**
 * The header line: "Lima · Zimmer 2 · 18 m² · CL-003".
 *
 * Segments are dropped when the field is absent rather than filled with a dash,
 * so the line never claims a room or a size the record does not carry. The
 * reference number is NOT part of it — it is its own labelled line, because a
 * bare identifier inside a title reads as part of the object's name.
 */
export function tenancyHeaderSegments(tn) {
  if (!tn) return [];
  const size =
    tn.room_size_m2 == null || tn.room_size_m2 === ""
      ? null
      : `${tn.room_size_m2} m²`;
  return [tn.unit_title, tn.room_name, size, tn.unit_short_unit_id]
    .map((v) => (v == null ? "" : String(v).trim()))
    .filter(Boolean);
}

/**
 * The one visible status.
 *
 * `display_status` is the server's derived status and the only one this tab
 * shows as a headline. The lifecycle vocabulary (Geschützt / Kündbar / …) stays
 * available in the detail area, where it reads as extra information rather than
 * as a competing verdict.
 */
export function tenancyStatusView(tn) {
  const key = String(tn?.display_status || "").toLowerCase();
  const label = tenancyDisplayStatusLabelDe(key);
  const tone =
    key === "active"
      ? "emerald"
      : key === "reserved"
        ? "sky"
        : key === "notice_given"
          ? "amber"
          : key === "cancelled"
            ? "rose"
            : "slate";
  return { key, label, tone };
}

/**
 * "Befristet", derived from contract data and no clock.
 *
 * An agreed end date on the tenancy is what makes it fixed-term; whether that
 * date has passed is a different question, and it is the one `display_status`
 * already answers. There is no `contract_type` / `is_fixed_term` column, so
 * this is an inference — but a time-independent one, which is why it survives
 * being rendered on a machine whose clock disagrees with Zurich.
 */
export function isFixedTerm(tn) {
  const v = tn?.move_out_date;
  return v != null && String(v).trim() !== "";
}

/** The period line, using the server's effective end. */
export function tenancyPeriodIso(tn) {
  return {
    from: tn?.move_in_date ? String(tn.move_in_date).slice(0, 10) : null,
    to: tenancyDisplayEndIso(tn),
  };
}

/**
 * The monthly figure, taken from the server.
 *
 * `monthly_revenue_equivalent` is the sum of the tenancy's active recurring
 * revenue lines, computed server-side against one business day. The tab used to
 * recompute it in the browser from the separately fetched revenue rows, which
 * is how it came to disagree with the Übersicht card. `null` stays null — an
 * absent figure is not zero.
 *
 * It is a SUM of recurring positions, not the base rent, so it must not be
 * labelled "Grundmiete".
 */
export function monthlyView(tn) {
  const v = tn?.monthly_revenue_equivalent;
  return v == null || v === "" ? null : Number(v);
}

/**
 * The tenant deposit, from the canonical column.
 *
 * `tenant_deposit_amount` is canonical; `deposit_amount` is legacy and is
 * neither displayed beside it nor summed with it. A missing deposit returns
 * null so the caller renders an em dash — `formatChfRent(null)` yields
 * "CHF 0.00" because `Number(null) === 0`, and a deposit that was never
 * recorded is not a deposit of nothing.
 */
export function depositView(tn) {
  const raw = tn?.tenant_deposit_amount;
  const amount = raw == null || raw === "" ? null : Number(raw);
  return {
    amount: Number.isFinite(amount) ? amount : null,
    type: tn?.tenant_deposit_type || null,
    provider: tn?.tenant_deposit_provider || null,
    received: Boolean(tn?.deposit_received),
    receivedAt: tn?.deposit_received_at
      ? String(tn.deposit_received_at).slice(0, 10)
      : null,
    returned: Boolean(tn?.deposit_returned),
    returnedAt: tn?.deposit_returned_at
      ? String(tn.deposit_returned_at).slice(0, 10)
      : null,
  };
}

const DEPOSIT_TYPE_LABELS = {
  cash: "Barkaution",
  insurance: "Mietkautionsversicherung",
  bank_guarantee: "Bankgarantie",
  none: "Keine",
};

export function depositTypeLabelDe(type) {
  const k = String(type || "").toLowerCase();
  return DEPOSIT_TYPE_LABELS[k] || (k ? type : null);
}

/**
 * How the viewed tenant relates to this tenancy.
 *
 * The tab lists tenancies the person only co-signs (`include_participant`), and
 * until now nothing distinguished those from the ones they hold. The helper for
 * exactly this case already existed and was imported but never called.
 */
export function viewerRoleLabel(tn, viewerTenantId) {
  if (!tn || !viewerTenantId) return null;
  if (String(tn.tenant_id) === String(viewerTenantId)) return "Hauptmieter";
  return viewerTenancyRoleLabelDe(tn, viewerTenantId);
}

/** German role labels, worded as TenancyParticipants.jsx already words them. */
const PARTICIPANT_ROLE_LABELS_DE = {
  primary_tenant: "Hauptmieter",
  co_tenant: "Co-Mieter",
  solidarhafter: "Solidarhafter",
};

/**
 * The people on this tenancy, with the role each holds ON THIS CONTRACT.
 *
 * Participants come from the list endpoint's batched `participants` array, so
 * this costs no request. A participant whose status is not active is kept but
 * flagged, because "was a co-tenant until March" is information, not noise.
 *
 * THE ROSTER IS THE ROLE TRUTH. This used to push the page's own tenant in as
 * "Hauptmieter" before reading the roster at all, which made every co-tenant
 * look like the contract holder on their own detail page — a role is a fact
 * about a tenancy, not about a person. `viewerTenant` is now only a last-resort
 * NAME source for the legacy branch below, and never a role.
 *
 * Person data lives at `participant.tenant` (`tenant_summary_dict`: id,
 * first_name, last_name, email, name, phone). Reading `participant.full_name` —
 * a key the API has never sent — is what rendered every co-tenant as an em dash.
 *
 * `phone` joined that summary later than the rest, so it is read defensively:
 * a tenancy list served by an older backend simply has no key there and the row
 * falls back to null, which the card already renders as "no line" rather than
 * as a dash.
 */
export function partiesView(tn, viewerTenant) {
  const rows = [];
  const seenTenantIds = new Set();

  const parts = Array.isArray(tn?.participants) ? tn.participants : [];
  for (const p of parts) {
    if (!p) continue;
    const tenantId = p.tenant_id != null ? String(p.tenant_id) : null;
    // One person, one row: the same human can hold a historical and an active
    // row on one tenancy, and the primary is reachable both through the roster
    // and through the legacy `tenancy.tenant_id` below.
    if (tenantId && seenTenantIds.has(tenantId)) continue;
    if (tenantId) seenTenantIds.add(tenantId);
    rows.push({
      id: tenantId ? `${tenantId}-${p.role}` : `party-${rows.length}`,
      tenantId,
      name: tenantDisplayName(p.tenant) || null,
      role: PARTICIPANT_ROLE_LABELS_DE[p.role] || "Co-Mieter",
      email: String(p.tenant?.email || "").trim() || null,
      phone: String(p.tenant?.phone || "").trim() || null,
      active: String(p.status || "active").toLowerCase() === "active",
      validTo: p.valid_to ? String(p.valid_to).slice(0, 10) : null,
      isPrimary: p.role === "primary_tenant",
    });
  }

  // Legacy branch: tenancies predating TenancyParticipant carry no roster at
  // all, and a roster can lack its `primary_tenant` row. `tenancy.tenant_id` is
  // documented on the model as the primary tenant for invoices and legacy
  // readers, so it is the contract holder when the roster does not name one.
  // The name comes from `primary_tenant_display`, which the API already
  // resolves centrally; the viewer stands in ONLY when the viewer IS that
  // person, so no name is ever invented for somebody else.
  const legacyPrimaryId = tn?.tenant_id != null ? String(tn.tenant_id) : null;
  if (legacyPrimaryId && !seenTenantIds.has(legacyPrimaryId)) {
    const viewerIsPrimary =
      viewerTenant != null && String(viewerTenant.id) === legacyPrimaryId;
    rows.unshift({
      id: `primary-${legacyPrimaryId}`,
      tenantId: legacyPrimaryId,
      role: PARTICIPANT_ROLE_LABELS_DE.primary_tenant,
      name:
        String(tn.primary_tenant_display_name || "").trim() ||
        (viewerIsPrimary ? tenantDisplayName(viewerTenant) : "") ||
        null,
      email:
        String(tn.primary_tenant_email || "").trim() ||
        (viewerIsPrimary ? String(viewerTenant.email || "").trim() : "") ||
        null,
      // Same three-step shape as the name and e-mail above: the tenancy's own
      // centrally resolved value first, then the viewer ONLY when the viewer is
      // that person, then nothing. The viewer step is the defensive one — it
      // covers a payload predating `primary_tenant_phone` — and it is guarded
      // so a number is never attributed to somebody it was not read from.
      phone:
        String(tn.primary_tenant_phone || "").trim() ||
        (viewerIsPrimary ? String(viewerTenant.phone || "").trim() : "") ||
        null,
      active: true,
      validTo: null,
      isPrimary: true,
    });
  }

  return rows;
}

/**
 * The move-in protocol's state, for the KPI row.
 *
 * The reference layout puts "Einzug abgeschlossen: Ja" here. Nothing defines
 * move-in completeness — it is three separate artefacts with three separate
 * authorities — so this names the ONE step the page can already prove: whether
 * a move-in inspection exists for this tenancy and carries a real signature
 * date. The card below shows all three steps, each from its own source.
 * `inspectionsList` is loaded at page level, so this costs nothing.
 */
export function moveInProtocolState(inspectionsList, tenancyId) {
  if (!Array.isArray(inspectionsList) || tenancyId == null) {
    return { key: "unknown", label: null, date: null };
  }
  const hit = inspectionsList.find(
    (i) =>
      i &&
      String(i.tenancy_id) === String(tenancyId) &&
      String(i.type || "").toLowerCase() === "move_in"
  );
  if (!hit) return { key: "none", label: "Nicht erstellt", date: null };
  if (hit.signed_at) {
    return {
      key: "signed",
      label: "Unterzeichnet",
      date: String(hit.signed_at).slice(0, 10),
    };
  }
  return { key: "created", label: "Erstellt", date: null };
}
